
import React, { useState } from 'react';
import { Card } from "@/components/ui/card";
import { useToast } from "@/hooks/use-toast";
import { AssessmentQuestion } from './AssessmentQuestion';

interface CustomerDiscoverySectionProps {
  onComplete: (data: { score: number; responses: Record<string, number> }) => void;
  initialData?: { score: number; responses: Record<string, number> };
}

export const CustomerDiscoverySection = ({ onComplete, initialData }: CustomerDiscoverySectionProps) => {
  const { toast } = useToast();
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [responses, setResponses] = useState<Record<string, number>>(
    initialData?.responses || {}
  );

  const questions = [
    {
      id: 'customerInsights',
      title: 'Customer Insights',
      question: 'How deeply do you understand your customers?',
      options: [
        { text: 'I regularly engage with customers, review feedback, and understand their experiences with our products.', points: 2 },
        { text: 'I conduct systematic user research, designing structured approaches to understand customer needs better than our competition.', points: 4 },
        { text: 'I establish customer discovery programs that generate ongoing insights throughout the organization. I create systems for continuous customer learning.', points: 7 },
        { text: 'I develop frameworks that transform how the entire company understands customers. I can accurately predict customer reactions to new concepts across product lines.', points: 10 }
      ],
      evidence: [
        { 
          title: 'Research Example',
          description: 'Share a specific example of your customer research approach'
        },
        {
          title: 'Key Insight',
          description: 'What surprising customer insight did you uncover?'
        }
      ]
    },
    {
      id: 'problemSpaceMastery',
      title: 'Problem Space Mastery',
      question: 'How do you identify and validate customer problems?',
      options: [
        { text: 'I gather customer pain points and feature requests, organizing them to inform roadmap priorities.', points: 2 },
        { text: 'I uncover underlying needs behind customer requests. I understand the jobs customers are trying to accomplish beyond what they explicitly ask for.', points: 4 },
        { text: 'I identify unmet needs in the market before customers can articulate them. I discover whitespace that competitors haven\'t recognized.', points: 7 },
        { text: 'I discover entirely new problem spaces worth solving that create new markets or product categories. My problem framing reshapes strategic direction for the company.', points: 10 }
      ],
      evidence: [
        {
          title: 'Problem Discovery',
          description: 'Describe how you identified a significant customer problem'
        },
        {
          title: 'Validation Method',
          description: 'How did you validate that this was a real problem worth solving?'
        }
      ]
    }
  ];

  const handleSelect = (points: number) => {
    const currentQuestionId = questions[currentQuestion].id;
    setResponses(prev => ({
      ...prev,
      [currentQuestionId]: points
    }));
  };

  const calculateScore = (responses: Record<string, number>): number => {
    return Object.values(responses).reduce((sum, points) => sum + points, 0);
  };

  const handleNext = () => {
    if (currentQuestion === questions.length - 1) {
      const finalData = {
        score: calculateScore(responses),
        responses
      };
      
      toast({
        title: "Section completed",
        description: "Your Customer Discovery & Understanding assessment has been saved.",
      });
      
      onComplete(finalData);
    } else {
      setCurrentQuestion(prev => prev + 1);
    }
  };

  const handlePrevious = () => {
    setCurrentQuestion(prev => Math.max(0, prev - 1));
  };

  const currentQuestionData = questions[currentQuestion];
  const selectedPoints = responses[currentQuestionData.id];

  return (
    <Card className="max-w-3xl mx-auto">
      <AssessmentQuestion
        title={currentQuestionData.title}
        question={currentQuestionData.question}
        options={currentQuestionData.options}
        evidence={currentQuestionData.evidence}
        onNext={handleNext}
        onPrevious={handlePrevious}
        onSelect={handleSelect}
        selectedPoints={selectedPoints}
        showPrevious={currentQuestion > 0}
      />
    </Card>
  );
};

export default CustomerDiscoverySection;
